import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import './StorePage.css';

function StorePage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showCart, setShowCart] = useState(false);
  const [selectedTutoring, setSelectedTutoring] = useState({});
  
  // Use global cart context
  const { cart, addToCart, removeFromCart, getTotalPrice, getTotalItems } = useCart();

  // Tutoring Packs Data
  const tutoringPacks = [
    {
      id: 'basic',
      name: 'Basic Pack',
      hours: 8,
      price: 99,
      description: '8 hours/month with certified tutor',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'advanced',
      name: 'Advanced Pack',
      hours: 20,
      price: 199,
      description: '20 hours/month with certified tutor',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'premium',
      name: 'Premium Pack',
      hours: 30,
      price: 299,
      description: '30 hours/month with certified tutor',
      color: 'from-amber-500 to-orange-600'
    }
  ];

  // Handle tutoring selection for a product
  const handleTutoringSelect = (productId, packId) => {
    setSelectedTutoring(prev => ({
      ...prev,
      [productId]: prev[productId] === packId ? null : packId
    }));
  };

  // Get selected tutoring pack for a product
  const getSelectedTutoringPack = (productId) => {
    const packId = selectedTutoring[productId];
    return packId ? tutoringPacks.find(p => p.id === packId) : null;
  };

  // Handle Checkout
  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty.');
      return;
    }
    alert('El sistema de pago está temporalmente desactivado para mantenimiento. Por favor contáctanos para completar tu compra.');
    // Redirect to contact page
    window.location.href = '/contact';
  };

  // Digital Products Data
  const products = [
    {
      id: 1,
      title: "Complete Spanish Learning Bundle",
      subtitle: "From Beginner to Advanced",
      description: "Master Spanish with our comprehensive digital package including 50+ interactive lessons, cultural insights, music analysis, and personalized exercises.",
      price: 149.99,
      originalPrice: 299.99,
      discount: 50,
      category: "bundle",
      level: "all",
      rating: 4.9,
      reviews: 2847,
      image: "/images/products/spanish.webp",
      features: [
        "50+ Interactive Video Lessons",
        "Cultural Context and Real-life Scenarios",
        "Music and Song Analysis",
        "Grammar Exercises and Quizzes",
        "Pronunciation Training",
        "Mobile App Access",
        "Lifetime Updates",
        "Certificate of Completion"
      ],
      digital: true
    },
    {
      id: 2,
      title: "Spanish Through Music Collection",
      subtitle: "Learn with Latin Rhythms",
      description: "Discover Spanish through the power of music! 100+ songs with translations, cultural context, and interactive exercises.",
      price: 79.99,
      originalPrice: 129.99,
      discount: 38,
      category: "music",
      level: "intermediate",
      rating: 4.8,
      reviews: 1923,
      image: "/images/products/music.webp",
      features: [
        "100+ Spanish Songs with Translations",
        "Cultural Background Stories",
        "Vocabulary Building Exercises",
        "Pronunciation Practice",
        "Genre Variety (Pop, Rock, Folk)",
        "Artist Interviews and Insights",
        "Karaoke Mode",
        "Offline Download"
      ],
      digital: true
    },
    {
      id: 3,
      title: "Business Spanish Mastery",
      subtitle: "Professional Communication",
      description: "Excel in Spanish-speaking business environments with specialized vocabulary, formal communication, and industry-specific content.",
      price: 99.99,
      originalPrice: 179.99,
      discount: 44,
      category: "business",
      level: "advanced",
      rating: 4.7,
      reviews: 856,
      image: "/images/products/business.webp",
      features: [
        "Business Vocabulary and Phrases",
        "Email and Letter Writing",
        "Meeting and Presentation Skills",
        "Industry-Specific Modules",
        "Cultural Business Etiquette",
        "Role-Playing Scenarios",
        "Networking Conversations",
        "Professional Certification"
      ],
      digital: true
    },

    {
      id: 5,
      title: "Travel Spanish Essentials",
      subtitle: "Speak Confidently While Traveling",
      description: "Everything you need to communicate effectively during your travels to Spanish-speaking countries.",
      price: 34.99,
      originalPrice: 69.99,
      discount: 43,
      category: "travel",
      level: "beginner",
      rating: 4.8,
      reviews: 3421,
      image: "/images/products/travel.webp",
      features: [
        "Essential Travel Phrases",
        "Airport and Hotel Conversations",
        "Restaurant and Shopping Dialogues",
        "Emergency Situations",
        "Cultural Tips and Etiquette",
        "Offline Phrasebook",
        "Audio Pronunciation",
        "Country-Specific Variations"
      ],
      digital: true
    },
    {
      id: 6,
      title: "Spanish Conversation Accelerator",
      subtitle: "Speak Fluently and Naturally",
      description: "Boost your speaking confidence with real-world conversations, idioms, and natural expressions used by native speakers.",
      price: 69.99,
      originalPrice: 119.99,
      discount: 42,
      category: "conversation",
      level: "intermediate",
      rating: 4.9,
      reviews: 1876,
      image: "/images/products/conversacion.webp",
      features: [
        "Real-life Conversation Scenarios",
        "Idioms and Colloquial Expressions",
        "Accent Reduction Training",
        "Confidence Building Exercises",
        "Native Speaker Audio",
        "Interactive Dialogues",
        "Cultural Context",
        "Speaking Challenges"
      ],
      digital: true
    },
    {
      id: 7,
      title: "Kids Spanish Adventure",
      subtitle: "Fun Learning for Children",
      description: "Make Spanish learning fun for kids with games, stories, songs, and interactive activities designed for young learners.",
      price: 49.99,
      originalPrice: 79.99,
      discount: 37,
      category: "kids",
      level: "beginner",
      rating: 4.9,
      reviews: 2156,
      image: "/images/products/kids.webp",
      features: [
        "Interactive Games and Activities",
        "Animated Stories",
        "Children's Songs",
        "Colorful Visual Learning",
        "Parent Progress Reports",
        "Age-Appropriate Content",
        "Reward System",
        "Safe Learning Environment"
      ],
      digital: true
    },
    {
      id: 8,
      title: "Spanish Literature and Culture",
      subtitle: "Explore Hispanic Heritage",
      description: "Dive deep into Spanish and Latin American literature, history, and cultural traditions while improving your language skills.",
      price: 89.99,
      originalPrice: 149.99,
      discount: 40,
      category: "culture",
      level: "advanced",
      rating: 4.7,
      reviews: 743,
      image: "/images/products/export.webp",
      features: [
        "Classic Literature Analysis",
        "Historical Context",
        "Cultural Traditions",
        "Author Biographies",
        "Reading Comprehension",
        "Discussion Questions",
        "Vocabulary Expansion",
        "Critical Thinking Exercises"
      ],
      digital: true
    },
 
 
    {
      id: 11,
      title: "Spanish Cooking and Recipes",
      subtitle: "Learn Through Cuisine",
      description: "Discover Spanish and Latin American cuisine while learning the language. Includes video recipes, vocabulary, and cultural context.",
      price: 44.99,
      originalPrice: 74.99,
      discount: 40,
      category: "food",
      level: "beginner",
      rating: 4.9,
      reviews: 2341,
      image: "/images/products/cocina.webp",
      features: [
        "50+ Authentic Recipes",
        "Video Cooking Tutorials",
        "Culinary Vocabulary",
        "Regional Specialties",
        "Shopping Lists in Spanish",
        "Cooking Techniques",
        "Cultural Food Traditions",
        "Downloadable Recipe Cards"
      ],
      digital: true
    },

  ];

  // Categories
  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'bundle', name: 'Complete Bundles' },
    { id: 'music', name: 'Music Learning' },
    { id: 'business', name: 'Business Spanish' },
    { id: 'grammar', name: 'Grammar Focus' },
    { id: 'travel', name: 'Travel Spanish' },
    { id: 'conversation', name: 'Conversation' },
    { id: 'kids', name: 'Kids Learning' },
    { id: 'culture', name: 'Culture and Literature' }
  ];

  // Levels
  const levels = [
    { id: 'all', name: 'All Levels' },
    { id: 'beginner', name: 'Beginner (A1-A2)' },
    { id: 'intermediate', name: 'Intermediate (B1-B2)' },
    { id: 'advanced', name: 'Advanced (C1-C2)' }
  ];

  // Filter and sort products
  const filteredProducts = products
    .filter(product => selectedCategory === 'all' || product.category === selectedCategory)
    .filter(product => selectedLevel === 'all' || product.level === selectedLevel || product.level === 'all')
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'popular':
        default:
          return b.reviews - a.reviews;
      }
    });

  // Handle adding product to cart with tutoring
  const handleAddToCart = (product) => {
    const tutoringPack = getSelectedTutoringPack(product.id);
    addToCart(product, tutoringPack);
    // Reset tutoring selection after adding to cart
    setSelectedTutoring(prev => ({
      ...prev,
      [product.id]: null
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Shared Header Component */}
      <Header 
        onCartClick={() => setShowCart(!showCart)} 
        showCart={true}
      />
      
      {/* Hero Section - Mismo estilo que Home */}
      <section 
        className="relative py-24 lg:py-32 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/spanish.learn.webp)',
          backgroundColor: '#FEF9C3'
        }}
      >
        {/* Background Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/95 via-secondary-50/92 to-primary-50/95"></div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-display font-bold text-gray-900 mb-6 leading-tight">
            Digital Spanish Learning Store
          </h1>
          <h2 className="text-2xl md:text-4xl font-display font-semibold text-gray-700 mb-8">
            Premium digital products to accelerate your journey
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Instant access, mobile compatible, and lifetime updates
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-lg">
              <span className="text-lg font-display font-semibold text-gray-900">Instant Access</span>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-lg">
              <span className="text-lg font-display font-semibold text-gray-900">Mobile Compatible</span>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-lg px-6 py-3 shadow-lg">
              <span className="text-lg font-display font-semibold text-gray-900">Lifetime Updates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Level and Sort Filters */}
            <div className="flex gap-4">
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              >
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600"
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 px-4">
            {filteredProducts.map((product) => (
              <div key={product.id} className="product-card bg-white rounded-xl shadow-lg overflow-visible">
                <div className="product-card-inner">
                  {/* Product Image with 3D Effect - MUCH LARGER */}
                  <div className="product-image-container relative h-96 p-6 bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="product-image-wrapper relative h-full">
                      <div className="product-frame"></div>
                      <div className="image-depth-effect relative h-full rounded-lg overflow-hidden">
                        <img 
                          src={product.image} 
                          alt={product.title}
                          className="product-image-3d w-full h-full object-contain"
                          style={{ objectFit: 'contain', maxHeight: '100%' }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23e5e7eb" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="60">📦</text></svg>';
                          }}
                        />
                        <div className="product-image-shine"></div>
                      </div>
                      <div className="product-reflection"></div>
                    </div>
                  </div>
                  
                  {/* Badges - Moved to not overlap text */}
                  <div className="absolute top-2 left-2 flex flex-col gap-2 z-10">
                    {product.bestseller && (
                      <span className="badge-bestseller text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        BESTSELLER
                      </span>
                    )}
                    {product.popular && (
                      <span className="badge-popular text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        POPULAR
                      </span>
                    )}
                    {/* Discount badge for Travel Spanish (id: 5) - Below POPULAR tag */}
                    {product.discount > 0 && product.id === 5 && (
                      <span className="badge-discount text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        -{product.discount}% OFF
                      </span>
                    )}
                  </div>

                  {/* Digital Badge - Top Right */}
                  <div className="absolute top-2 right-2 z-10 flex flex-col gap-2 items-end">
                    <span className="bg-white/95 backdrop-blur-sm text-primary-600 px-3 py-1 rounded-full text-xs font-semibold shadow-lg border border-primary-200">
                      Digital
                    </span>
                    {/* Discount badge for all products except Travel Spanish (id: 5) */}
                    {product.discount > 0 && product.id !== 5 && (
                      <span className="badge-discount text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        -{product.discount}% OFF
                      </span>
                    )}
                  </div>
                  

                  {/* Product Info */}
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {product.title}
                      </h3>
                      <p className="text-sm text-primary-600 font-medium">
                        {product.subtitle}
                      </p>
                    </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Rating */}
                  <div className="flex items-center mb-4">
                    <div className="flex text-yellow-400 star-rating">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'}>
                          *
                        </span>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600 ml-2">
                      {product.rating} ({product.reviews.toLocaleString()} reviews)
                    </span>
                  </div>

                  {/* Features List - show all features */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Key Features:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <span className="feature-check mr-2">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">
                        ${product.price}
                      </span>
                      {product.originalPrice > product.price && (
                        <span className="text-sm text-gray-500 line-through ml-2">
                          ${product.originalPrice}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {product.level === 'all' ? 'All Levels' : product.level.charAt(0).toUpperCase() + product.level.slice(1)}
                    </span>
                  </div>

                  {/* Personal Tutoring Options */}
                  <div className="mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                    <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                      Add Personal Tutoring (Optional):
                    </p>
                    <div className="space-y-2">
                      {tutoringPacks.map((pack) => (
                        <button
                          key={pack.id}
                          onClick={() => handleTutoringSelect(product.id, pack.id)}
                          className={`w-full p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between ${
                            selectedTutoring[product.id] === pack.id
                              ? 'border-primary-500 bg-primary-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${pack.color} flex items-center justify-center text-white text-xs font-bold mr-3`}>
                              {pack.hours}h
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 text-sm">{pack.name}</p>
                              <p className="text-xs text-gray-500">{pack.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary-600">+${pack.price}</p>
                            <p className="text-xs text-gray-500">/month</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    {selectedTutoring[product.id] && (
                      <div className="mt-3 p-2 bg-primary-100 rounded-lg text-center">
                        <p className="text-sm text-primary-700 font-medium">
                          Total: ${(product.price + getSelectedTutoringPack(product.id).price).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="add-to-cart-btn w-full text-white py-3 rounded-lg font-semibold flex items-center justify-center relative z-10"
                    >
                      {selectedTutoring[product.id] 
                        ? `Add to Cart - $${(product.price + getSelectedTutoringPack(product.id).price).toFixed(2)}`
                        : `Add to Cart - $${product.price}`
                      }
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                No products found
              </h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your filters to see more products.
              </p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedLevel('all');
                }}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition"
              >
                Show All Products
              </button>
            </div>
          )}
        </div>
      </section>
      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold">Shopping Cart</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.cartItemId} className="flex items-start space-x-4 p-4 border rounded-lg">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          <img 
                            src={item.image} 
                            alt={item.title || item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span class="text-2xl">Item</span>`;
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm">{item.title || item.name}</h3>
                          {item.type === 'game' ? (
                            <p className="text-gray-600 text-xs capitalize">{item.period}</p>
                          ) : (
                            <p className="text-gray-600 text-xs">{item.subtitle}</p>
                          )}
                          {item.tutoringPack && (
                            <div className="mt-1 flex items-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r ${item.tutoringPack.color} text-white`}>
                                Tutoring: {item.tutoringPack.name} ({item.tutoringPack.hours}h/mo)
                              </span>
                            </div>
                          )}
                          <div className="mt-1">
                            <p className="font-bold text-primary-600">${item.totalPrice.toFixed(2)}</p>
                            {item.tutoringPack && (
                              <p className="text-xs text-gray-500">
                                Course: ${item.price} + Tutoring: ${item.tutoringPack.price}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.cartItemId)}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="border-t p-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-primary-600">
                      ${getTotalPrice().toFixed(2)}
                    </span>
                  </div>
                  <button 
                    onClick={handleCheckout}
                    className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition shadow-lg"
                  >
                    Proceed to Checkout
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Complete your safe order
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Our Digital Products */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Why Choose Our Digital Products?
            </h2>
            <p className="text-lg text-gray-600">
              Premium quality, instant access, and proven results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl"></span>
              </div>
              <h3 className="text-lg font-display font-semibold mb-2">Instant Access</h3>
              <p className="text-gray-600 text-sm">
                Download immediately after purchase. Start learning right away!
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl"></span>
              </div>
              <h3 className="text-lg font-display font-semibold mb-2">Mobile Compatible</h3>
              <p className="text-gray-600 text-sm">
                Learn on any device - phone, tablet, or computer. Sync your progress.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl"></span>
              </div>
              <h3 className="text-lg font-display font-semibold mb-2">Expert Created</h3>
              <p className="text-gray-600 text-sm">
                Developed by native speakers and language learning experts.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl"></span>
              </div>
              <h3 className="text-lg font-display font-semibold mb-2">Lifetime Updates</h3>
              <p className="text-gray-600 text-sm">
                Get free updates and new content added to your products forever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              What Our Students Say
            </h2>
            <p className="text-lg text-gray-600">
              Join thousands of successful Spanish learners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="testimonial-card bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold">
                  S
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">Sarah Johnson</h4>
                  <p className="text-sm text-gray-600">Marketing Manager, USA</p>
                </div>
              </div>
              <div className="flex text-yellow-400 mb-3 star-rating">
                ⭐⭐⭐⭐⭐
              </div>
              <p className="text-gray-700 text-sm">
                "The Complete Spanish Learning Bundle transformed my language skills! The music collection made learning so enjoyable. I'm now conversational after just 3 months!"
              </p>
            </div>

            <div className="testimonial-card bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
                  M
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">Michael Chen</h4>
                  <p className="text-sm text-gray-600">Software Engineer, Canada</p>
                </div>
              </div>
              <div className="flex text-yellow-400 mb-3 star-rating">
                ⭐⭐⭐⭐⭐
              </div>
              <p className="text-gray-700 text-sm">
                "Business Spanish Mastery helped me land a promotion! The professional vocabulary and cultural insights were exactly what I needed for my career."
              </p>
            </div>

            <div className="testimonial-card bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold">
                  E
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold">Emma Rodriguez</h4>
                  <p className="text-sm text-gray-600">Teacher, Australia</p>
                </div>
              </div>
              <div className="flex text-yellow-400 mb-3 star-rating">
                ⭐⭐⭐⭐⭐
              </div>
              <p className="text-gray-700 text-sm">
                "I used the Travel Spanish Essentials before my trip to Mexico. It was perfect! I could communicate confidently and even made local friends."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Money Back Guarantee */}
      <section className="py-20" style={{ backgroundColor: '#FF6B6B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-6"></div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            30-Day Money-Back Guarantee
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Try our products risk-free. If you're not completely satisfied, we'll refund your money - no questions asked.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
              <span className="font-semibold">✓ Full Refund</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
              <span className="font-semibold">✓ No Questions Asked</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
              <span className="font-semibold">✓ Keep Bonus Materials</span>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Footer Component */}
      <Footer />
    </div>
  );
}

export default StorePage;